import React from "react"

import {
  DetailRow,
  isNonEmptyJson,
  JsonBlock,
  KeyValueChips,
} from "@/components/detail/DetailFields"
import type { JsonFieldValue, JsonObject } from "@/types/json"
import {
  getScriptTurnText,
  isDtmfScriptTurn,
  type ScriptTurn,
} from "@/types/ScriptTurn"

export interface TestCaseData {
  id?: string
  description?: string | null
  inputStr?: string | null
  /** A Script case's exact turns; it carries these instead of an input. */
  scriptTurns?: ScriptTurn[] | null
  expectedBehaviors?: string[]
  expectedOutputJson?: JsonFieldValue
  simulationMetadataInput?: JsonObject | null
  metricInput?: JsonFieldValue
  testCaseParams?: Record<string, string>
}

/**
 * The key/value pairs a field describes, or null when it does not describe any.
 *
 * A test case's expected output reaches the frontend either as an object or as
 * a JSON string, depending on how it was authored, so the string form is parsed
 * rather than rendered raw — otherwise it lands on screen as escaped JSON, and
 * enumerating it directly would yield one entry per character.
 */
function toKeyValueEntries(
  value: JsonFieldValue | null | undefined,
): [string, unknown][] | null {
  if (value == null) {
    return null
  }

  let parsed: unknown = value
  if (typeof value === "string") {
    const trimmed = value.trim()
    if (!trimmed) {
      return null
    }
    try {
      parsed = JSON.parse(trimmed)
    } catch {
      return null
    }
  }

  if (parsed == null || typeof parsed !== "object" || Array.isArray(parsed)) {
    return null
  }

  const entries = Object.entries(parsed as Record<string, unknown>)
  return entries.length > 0 ? entries : null
}

/**
 * The script turns a stored test case row carries, or undefined when it has
 * none. Live records expose them as `script_turns`; version snapshots keep the
 * stored row, where they sit inside `simulation_metadata_input`. Rows reach the
 * drawers as loose JSON, so only an array is taken.
 */
export function readScriptTurns(
  row: Record<string, unknown> | undefined,
): ScriptTurn[] | undefined {
  const metadata = row?.simulation_metadata_input
  const value =
    row?.script_turns ??
    (typeof metadata === "object" && metadata !== null
      ? (metadata as Record<string, unknown>).script_turns
      : undefined)
  return Array.isArray(value) && value.length > 0
    ? (value as ScriptTurn[])
    : undefined
}

function withoutListedScript(
  metadata: JsonObject | null | undefined,
  scriptTurns: ScriptTurn[] | null | undefined,
): JsonObject | null | undefined {
  if (!scriptTurns?.length || !metadata || !("script_turns" in metadata)) {
    return metadata
  }
  const { script_turns: _listed, ...rest } = metadata
  return rest
}

/**
 * A script turn as one line of text: what the persona says, the keys it
 * presses, or the raw entry for a turn type this frontend does not know yet.
 */
function formatScriptTurn(turn: ScriptTurn): string {
  const text = getScriptTurnText(turn)
  if (text !== null) {
    return text
  }
  if (isDtmfScriptTurn(turn)) {
    return `Press ${turn.digits}`
  }
  return JSON.stringify(turn)
}

/**
 * Whether a test case has any field this renders, so callers can skip framing
 * a card around nothing — an id alone describes a row without describing it.
 */
export function hasTestCaseDetails(testCase: TestCaseData): boolean {
  return (
    Boolean(testCase.description?.trim()) ||
    Boolean(testCase.inputStr?.trim()) ||
    (testCase.scriptTurns?.length ?? 0) > 0 ||
    (testCase.expectedBehaviors?.length ?? 0) > 0 ||
    isNonEmptyJson(testCase.expectedOutputJson) ||
    Object.keys(testCase.testCaseParams ?? {}).length > 0 ||
    isNonEmptyJson(testCase.simulationMetadataInput) ||
    isNonEmptyJson(testCase.metricInput)
  )
}

/**
 * One test case's fields, shared by the test set drawer and the surfaces that
 * show the test case a run or review item came from, so a reviewer reads the
 * same presentation wherever the test case appears.
 *
 * This covers what the test case defined. What a conversation carried at run
 * time — its custom metadata, initialization output, and presimulation
 * attributes — belongs to the run rather than to the case, and is read out by
 * the conversation metadata panel instead.
 */
export function TestCaseDetails({ testCase }: { testCase: TestCaseData }) {
  const expectedOutputEntries = toKeyValueEntries(testCase.expectedOutputJson)
  const parameterEntries = Object.entries(testCase.testCaseParams ?? {})
  // A stored row keeps its script inside the metadata; the Script row already
  // lists it, so the metadata block leaves it out rather than repeat it as JSON.
  const simulationMetadata = withoutListedScript(
    testCase.simulationMetadataInput,
    testCase.scriptTurns,
  )

  return (
    <>
      {testCase.description?.trim() ? (
        <DetailRow label="Description">
          <p className="whitespace-pre-wrap break-words">
            {testCase.description}
          </p>
        </DetailRow>
      ) : null}

      {testCase.inputStr?.trim() ? (
        <DetailRow label="Scenario">
          <pre className="font-mono text-xs text-coval-text whitespace-pre-wrap break-words max-w-full">
            {testCase.inputStr}
          </pre>
        </DetailRow>
      ) : null}

      {testCase.scriptTurns && testCase.scriptTurns.length > 0 ? (
        <DetailRow label="Script">
          <ol className="list-decimal list-outside pl-5 space-y-1">
            {testCase.scriptTurns.map((turn, i) => (
              <li key={i} className="text-sm whitespace-pre-wrap break-words">
                {formatScriptTurn(turn)}
              </li>
            ))}
          </ol>
        </DetailRow>
      ) : null}

      {testCase.expectedBehaviors && testCase.expectedBehaviors.length > 0 ? (
        <DetailRow label="Expected Behaviors">
          <ul className="list-disc list-outside pl-5 space-y-1">
            {testCase.expectedBehaviors.map((behavior, i) => (
              <li key={i} className="text-sm break-words">
                {behavior}
              </li>
            ))}
          </ul>
        </DetailRow>
      ) : null}

      {isNonEmptyJson(testCase.expectedOutputJson) ? (
        <DetailRow label="Variables">
          {expectedOutputEntries ? (
            <KeyValueChips entries={expectedOutputEntries} />
          ) : (
            <JsonBlock value={testCase.expectedOutputJson} />
          )}
        </DetailRow>
      ) : null}

      {parameterEntries.length > 0 ? (
        <DetailRow label="Parameters">
          <KeyValueChips entries={parameterEntries} />
        </DetailRow>
      ) : null}

      {isNonEmptyJson(simulationMetadata) ? (
        <DetailRow label="Simulation Metadata">
          <JsonBlock value={simulationMetadata} />
        </DetailRow>
      ) : null}

      {isNonEmptyJson(testCase.metricInput) ? (
        <DetailRow label="Metric Input">
          <JsonBlock value={testCase.metricInput} />
        </DetailRow>
      ) : null}
    </>
  )
}
